import React, { useEffect, useRef, useState } from 'react';
import { WarningCircle } from '@phosphor-icons/react';

/**
 * Aviso de sessão expirada — EM CIMA da tela, que continua montada. Mesmo
 * aviso do hub e do CRM.
 *
 * "Continuar conectado" renova pelo MSAL silencioso (a conta Microsoft
 * continua no cache do navegador) e fecha o aviso: o operador repete só a
 * última ação. "Entrar de novo" é o caminho antigo, agora por escolha dele —
 * e volta para a mesma página depois do login.
 *
 * Recebe `sessao` por props e não usa hook de rota (o AuthProvider fica fora
 * do BrowserRouter — ver SessaoPill).
 *
 * Desenho do CareOS: véu = .modal__veu (--care-bg-overlay + blur 2px, grid
 * centralizado, z modal), cartão = .ui-card elevação lg em min(440px, 100%),
 * botões = .ui-botao sólido e fantasma. Teclado: abre com o foco no botão
 * principal, o Tab fica preso entre os dois botões e, ao fechar, o foco volta
 * para onde estava. O Esc não fecha: a sessão segue expirada.
 */
const SessaoExpiradaModal = ({ sessao }) => {
  const [tentando, setTentando] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const expirada = !!sessao?.expirada;
  const cartaoRef = useRef(null);
  const principalRef = useRef(null);

  // Fechou (renovou, por aqui ou por outra aba): a próxima expiração abre
  // limpa, não já dizendo "não consegui renovar".
  useEffect(() => {
    if (!expirada) setFalhou(false);
  }, [expirada]);

  // Ao abrir, leva o foco para "Continuar conectado"; ao fechar, devolve-o ao
  // elemento da página que o tinha.
  useEffect(() => {
    if (!expirada) return undefined;
    const anterior = typeof document !== 'undefined' ? document.activeElement : null;
    if (principalRef.current) principalRef.current.focus();
    return () => {
      if (anterior && typeof anterior.focus === 'function' && document.contains(anterior)) anterior.focus();
    };
  }, [expirada]);

  // Terminou a tentativa: o botão voltou a ser focável. Se o foco caiu fora do
  // cartão (botão desabilitado perde o foco), devolve-o ao principal.
  useEffect(() => {
    if (!expirada || tentando) return;
    const cartao = cartaoRef.current;
    if (cartao && !cartao.contains(document.activeElement) && principalRef.current) principalRef.current.focus();
  }, [expirada, tentando]);

  if (!sessao || !expirada) return null;

  const continuar = async () => {
    setTentando(true);
    setFalhou(false);
    let ok = false;
    try { ok = await sessao.renovar(); } catch { ok = false; }
    setTentando(false);
    if (!ok) setFalhou(true);
  };

  // Prende o Tab entre os botões habilitados do cartão (ou no próprio cartão,
  // enquanto os dois estão desabilitados durante a renovação).
  const prenderTab = (e) => {
    if (e.key !== 'Tab') return;
    const cartao = cartaoRef.current;
    if (!cartao) return;
    const focaveis = Array.from(cartao.querySelectorAll('button:not([disabled])'));
    if (focaveis.length === 0) {
      e.preventDefault();
      cartao.focus();
      return;
    }
    const primeiro = focaveis[0];
    const ultimo = focaveis[focaveis.length - 1];
    const ativo = document.activeElement;
    if (e.shiftKey && (ativo === primeiro || !cartao.contains(ativo) || ativo === cartao)) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && (ativo === ultimo || !cartao.contains(ativo))) {
      e.preventDefault();
      primeiro.focus();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sessao-expirada-titulo"
      aria-describedby="sessao-expirada-descricao"
      onKeyDown={prenderTab}
      style={{
        position: 'fixed', inset: 0, zIndex: 'var(--care-z-modal)',
        display: 'grid', placeItems: 'center', padding: 'var(--care-space-7)',
        background: 'var(--care-bg-overlay)',
        backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
      }}
    >
      <div
        ref={cartaoRef}
        tabIndex={-1}
        className="ui-card"
        data-elevacao="lg"
        style={{
          width: 'min(440px, 100%)', padding: 'var(--care-space-8)', textAlign: 'center',
          // O .ui-card empilha com gap; aqui o ritmo é o de antes (8 / 16 / 4),
          // dado pelas margens de cada bloco.
          gap: 0,
        }}
      >
        <h2
          id="sessao-expirada-titulo"
          style={{
            margin: '0 0 var(--care-space-4)',
            fontFamily: 'var(--care-font-sans)',
            fontSize: 'var(--care-text-xl)',
            fontWeight: 'var(--care-weight-semibold)',
            lineHeight: 'var(--care-leading-snug)',
            letterSpacing: 'var(--care-tracking-heading)',
            color: 'var(--care-text-primary)',
          }}
        >
          Sua sessão expirou
        </h2>
        {/* A descrição normal fica fora da região viva; a falha entra DENTRO de
            uma região role="alert" sempre montada, que é o que os leitores de
            tela anunciam de forma confiável (trocar o papel de um nó já
            existente não é). As duas têm o mesmo id, uma de cada vez, para o
            aria-describedby do diálogo. */}
        {!falhou && (
          <p
            id="sessao-expirada-descricao"
            style={{
              margin: '0 0 var(--care-space-6)',
              fontSize: 'var(--care-text-base)',
              lineHeight: 'var(--care-leading-normal)',
              color: 'var(--care-text-secondary)',
            }}
          >
            O que está na tela continua aqui. Renove a sessão para seguir de onde parou — só a última ação precisa ser repetida.
          </p>
        )}
        <div role="alert">
          {falhou && (
            <p
              id="sessao-expirada-descricao"
              className="ui-aviso"
              data-tom="negativo"
              style={{ justifyContent: 'center', textAlign: 'left', margin: '0 0 var(--care-space-6)' }}
            >
              <WarningCircle size={16} weight="bold" aria-hidden="true" />
              <span>Não consegui renovar com a Microsoft. O que está na tela continua aqui; entre de novo para seguir.</span>
            </p>
          )}
        </div>
        <button
          ref={principalRef}
          type="button"
          className="ui-botao"
          data-variante="solido"
          onClick={() => { continuar(); }}
          disabled={tentando}
          aria-disabled={tentando}
          aria-busy={tentando}
          style={{ width: '100%', minHeight: 44 }}
        >
          {tentando ? 'Renovando…' : 'Continuar conectado'}
        </button>
        <button
          type="button"
          className="ui-botao"
          data-variante="fantasma"
          onClick={() => { sessao.entrarDeNovo(); }}
          disabled={tentando}
          aria-disabled={tentando}
          style={{ width: '100%', minHeight: 44, marginTop: 'var(--care-space-2)' }}
        >
          Entrar de novo
        </button>
      </div>
    </div>
  );
};

export default SessaoExpiradaModal;
